// Phase-3 Slice 27 + 28 + 34 + 35 — Rename preview/apply/undo service.
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 27 + § Slice 28 + § Slice 34
//      + § Slice 35.
//
// Thin orchestrator over `planRename` (preview) + `executeRename` (apply).
// Production callers go through this module; the preview dialog's open
// action populates `useRenamePreviewStore` via `previewRename`, and the
// dialog's Apply button calls `applyRename` from the same store.
//
// Slice 34 (amended 2026-08-23 — NAV1 amendment): after a successful apply,
// the open query tabs bound to the renamed object's own connection + database
// are REWRITTEN, undoably, and each gets a notice reporting what changed. That
// whole pass lives in `renamePostApplyTabPass.ts`; this module just calls it
// with the batch + scope. Its seams are dependency-injected so unit tests don't
// need to stand up the workspace store.
//
// Slice 35: after a successful apply, push the batch onto `useRenameUndoLog`
// — together with the DATABASE the rename ran in, which undo replays against
// (the caller's database is only the fallback; see `undoLastRename`) — so a
// subsequent `undoLastRename(connectionId)` can pop it and run the
// reverse-ALTER `undoBatch` via `executeRename`. One-level undo only — undo
// does NOT itself record into the log (no redo in v1). The post-undo
// courtesy notice fires with the swapped identifiers so tabs that reference
// the *new* (now-undone) name surface a banner.

import type {
  AlterStatement,
  ObjectBodyEdit,
  RenameBatch,
  RenameResult,
} from '../types';

import { planRename, type PlanRenameDeps, type PlanRenameInput, type PlanRenameResult } from './renamePlanner';
import {
  executeRename,
  type ExecuteRenameDeps,
} from './renameExecutor';
import { trackNavFeature } from '../analytics/trackNavFeature';
import {
  runPostApplyTabPass,
  type PostApplyTabPassDeps,
} from './renamePostApplyTabPass';
import { catalogDestination, destinationOf } from './renameDestination';
import { useRenameUndoLog, type RenameUndoEntry } from './renameUndoLog';

export type PreviewRenameDeps = PlanRenameDeps;

export async function previewRename(
  input: PlanRenameInput,
  deps?: PreviewRenameDeps,
): Promise<PlanRenameResult> {
  return planRename(input, deps);
}

export type ApplyRenameInput = {
  readonly connectionId: string;
  readonly database: string;
  readonly batch: RenameBatch;
};

export type ApplyRenameDeps = ExecuteRenameDeps & PostApplyTabPassDeps & {
  /** Records the applied batch into the undo log, together with the database
   *  the rename ran in. Defaults to `useRenameUndoLog.recordApply`. Test
   *  callers can pass a no-op to bypass logging. */
  readonly recordUndo?: (connectionId: string, batch: RenameBatch, database: string) => void;
};

export async function applyRename(
  input: ApplyRenameInput,
  deps?: ApplyRenameDeps,
): Promise<RenameResult> {
  const result = await executeRename(input, deps);
  if (result.status === 'success') {
    const record = deps?.recordUndo ?? defaultRecordUndo;
    // The database travels WITH the entry: a rename typed as `B.dbo.users`
    // from an `A` tab ran in `B`, and its undo must too.
    record(input.connectionId, input.batch, input.database);
    runPostApplyTabPass(input.batch, { connectionId: input.connectionId, database: input.database }, deps);
  }
  return result;
}

export type UndoLastRenameInput = {
  readonly connectionId: string;
  /** FALLBACK database only — the caller's context (the command palette sends
   *  the active tab's). The entry's own recorded database wins when it has one;
   *  see {@link undoLastRename}. */
  readonly database: string;
};

export type UndoLastRenameResult =
  | { readonly status: 'no-entry' }
  | {
      readonly status: 'executed';
      readonly result: RenameResult;
      readonly undoneBatch: RenameBatch;
    };

export type UndoLastRenameDeps = ApplyRenameDeps & {
  /** Pops the most-recent ENTRY — batch plus the database the rename ran in.
   *  Defaults to `useRenameUndoLog.popLast`. Test callers can stub. */
  readonly popUndoEntry?: (connectionId: string) => RenameUndoEntry | undefined;
};

export async function undoLastRename(
  input: UndoLastRenameInput,
  deps?: UndoLastRenameDeps,
): Promise<UndoLastRenameResult> {
  const pop = deps?.popUndoEntry ?? defaultPopUndoEntry;
  const entry = pop(input.connectionId);
  if (!entry) return { status: 'no-entry' };
  const original = entry.batch;

  // BEHAVIOUR: undo runs in the database the rename RAN in, not the one the
  // caller is looking at. F2 routes a rename at the database the cursor's chain
  // named (`renameProvider.ts`), so a rename applied in `B` from an `A` tab
  // would otherwise be reversed in `A` — and the reverse ALTER is a bare
  // `EXEC sp_rename '[dbo].[users]', …` on SQL Server, which would then rename
  // `A`'s same-named object. The caller's database stays the fallback for an
  // entry that recorded none.
  const database = entry.database ?? input.database;

  const inverseBatch = makeInverseBatch(original);

  // Run the reverse alters via the executor. Do NOT route through
  // `applyRename` — undo must NOT record itself into the log (no redo in v1).
  const result = await executeRename(
    {
      connectionId: input.connectionId,
      database,
      batch: inverseBatch,
    },
    deps,
  );

  if (result.status === 'success') {
    trackNavFeature({ name: 'feature:navfeatures_rename_undo', props: { kind: original.target.kind } });
    runPostApplyTabPass(inverseBatch, { connectionId: input.connectionId, database }, deps);
  }

  return { status: 'executed', result, undoneBatch: original };
}

/** Builds an inverse `RenameBatch` from an applied batch by treating the
 *  original `undoBatch` as the new forward sequence. The first entry is the
 *  schema rename; the rest are body-rewrite ALTERs that pair with the
 *  original `objectEdits` in order.
 *
 *  The two DESTINATIONS swap with the alters, they are not re-derived: the
 *  inverse's destination is the one the planner already computed for
 *  `undoBatch[0]` (`renameDestination.resolveRestoreDestination`), and its own
 *  undo destination is the forward one. That is the whole fix for the
 *  direction that kept drifting — this function used to swap two identifiers
 *  and leave `tabRewritePlanner` to PREDICT a catalog spelling out of
 *  `target.fromIdentifier`, an identity that carries `quoted: true` but no
 *  `quoting`. A case-exact original therefore folded (PostgreSQL `Id` → `id`,
 *  Oracle `row_id` → `ROW_ID`) while the undo ALTER put the exact bytes back,
 *  so every open buffer named a column the server does not have.
 *
 *  Both fields are REQUIRED on `RenameBatch`, so the `??` below is unreachable
 *  from typed code; it stays for the untyped E2E seam that hands
 *  `undoLastRename` a batch literal written inside `page.evaluate` (see
 *  {@link catalogDestination}), and it reads the `target` identifiers as the
 *  catalog spellings they are documented to be — still no prediction. */
function makeInverseBatch(original: RenameBatch): RenameBatch {
  const reverseAlters = original.undoBatch;
  const inverseSchemaAlter: AlterStatement = reverseAlters[0] ?? {
    sql: '-- no-op: original batch had no undo schema-rename',
    purpose: 'schema-rename',
  };
  const inverseBodyAlters: readonly AlterStatement[] = reverseAlters.slice(1);

  // Internal-invariant guard: a well-formed RenameBatch has exactly one body
  // undo ALTER per objectEdit (undoBatch = [schemaHead, ...bodyUndos]), so
  // inverseBodyAlters.length must equal objectEdits.length. A mismatch means
  // the undo-log entry is corrupt or was produced by a planner bug — proceeding
  // would silently ALTER the wrong object, which is strictly worse than failing
  // fast.
  if (inverseBodyAlters.length !== original.objectEdits.length) {
    throw new Error(
      `Cannot safely invert rename batch: internal invariant violated — ` +
      `undoBatch body-alter count (${inverseBodyAlters.length}) does not match ` +
      `objectEdits count (${original.objectEdits.length}). ` +
      `The undo-log entry appears corrupt and cannot be safely inverted. ` +
      `Please report this as a bug.`,
    );
  }

  const inverseObjectEdits: ObjectBodyEdit[] = inverseBodyAlters.map((alter, idx) => {
    const originalEdit = original.objectEdits[idx]!;
    return {
      objectKind: originalEdit.objectKind,
      objectName: originalEdit.objectName,
      // Post-undo, what was "newBody" becomes the body we're moving away
      // from, and what was "originalBody" becomes the target body. Body
      // text isn't read by the executor; preserved here for callers that
      // want to render the inverse plan.
      originalBody: originalEdit.newBody,
      newBody: originalEdit.originalBody,
      alter,
    };
  });

  const forwardSequence: AlterStatement[] = [
    original.schemaAlter,
    ...original.objectEdits.map((e) => e.alter),
  ];

  const destination =
    original.undoDestination ?? catalogDestination(original.target.fromIdentifier);

  return {
    target: {
      fromIdentifier: original.target.toIdentifier,
      // Read off the destination below, exactly as `planRename` does — the
      // `RenameBatch.target.toIdentifier` invariant holds in both directions.
      toIdentifier: destination.identifier,
      kind: original.target.kind,
      containingTable: original.target.containingTable,
    },
    schemaAlter: inverseSchemaAlter,
    objectEdits: inverseObjectEdits,
    unrewritable: [],
    // For symmetry: the inverse's undoBatch is the original's forward
    // sequence. Not used in v1 (no redo) but keeps the type valid.
    undoBatch: forwardSequence,
    destination,
    undoDestination: destinationOf(original),
    dialect: original.dialect,
  };
}

function defaultRecordUndo(connectionId: string, batch: RenameBatch, database: string): void {
  useRenameUndoLog.getState().recordApply(connectionId, batch, database);
}

function defaultPopUndoEntry(connectionId: string): RenameUndoEntry | undefined {
  return useRenameUndoLog.getState().popLast(connectionId);
}
